// src/utils/fileSecurityValidation.js

/**
 * File Security Validation Module
 * Enforces strict file type, file extension, and file size limits
 * to prevent WASA vulnerabilities (malicious file uploads, storage abuse,
 * resource exhaustion, and bypass attacks).
 */

export const ALLOWED_DOCUMENT_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];
export const ALLOWED_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/pjpeg",
];

// 10 MB per file limit
export const MAX_DOCUMENT_FILE_SIZE = 10 * 1024 * 1024;
// Minimum 1 byte to reject 0-byte / empty files
export const MIN_DOCUMENT_FILE_SIZE = 1;
// Maximum batch upload size (50 MB)
export const MAX_TOTAL_UPLOAD_SIZE = 20 * 1024 * 1024;

// Dangerous extensions that must never be permitted under any circumstance
const DANGEROUS_EXTENSIONS_PATTERN =
  /\.(exe|bat|cmd|sh|bash|php|phtml|php[0-9]|js|mjs|vbs|jar|war|py|pl|cgi|html|htm|svg|xml|asp|aspx|jsp|jspx|dll|bin|com|scr|msi|ps1|psm1|htaccess|config|reg|apk|ipa)$/i;

// Double extension detection pattern: e.g. file.php.png or script.exe.pdf
const SUSPICIOUS_DOUBLE_EXT_PATTERN =
  /\.(exe|bat|cmd|sh|bash|php|phtml|php[0-9]|js|mjs|vbs|jar|war|py|pl|cgi|html|htm|svg|xml|asp|aspx|jsp|jspx|dll|bin|com|scr|msi|ps1|psm1)\.[a-z0-9]+$/i;

// Null byte or control char injection in filename
const MALICIOUS_FILENAME_CHARS = /[\x00-\x1F\x7F\u200B-\u200D\uFEFF]/;

/**
 * Validates a single document file for clinical assessment upload.
 * @param {File|Blob} file - The file to validate
 * @param {Object} options - Custom validation options
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateDocumentFile = (file, options = {}) => {
  if (!file) {
    return { valid: false, error: "No file provided for validation." };
  }

  const maxSize = options.maxSize || MAX_DOCUMENT_FILE_SIZE;
  const minSize = options.minSize || MIN_DOCUMENT_FILE_SIZE;
  const allowedExts = options.allowedExtensions || ALLOWED_DOCUMENT_EXTENSIONS;
  const allowedMimes = options.allowedMimes || ALLOWED_DOCUMENT_MIME_TYPES;

  const fileName = file.name || "";

  // 1. File Name & Control Character Checks
  if (fileName) {
    if (MALICIOUS_FILENAME_CHARS.test(fileName) || fileName.includes("%00")) {
      return {
        valid: false,
        error: `File "${fileName}" contains invalid or prohibited characters.`,
      };
    }

    if (
      fileName.includes("..") ||
      fileName.includes("/") ||
      fileName.includes("\\")
    ) {
      return {
        valid: false,
        error: `File "${fileName}" contains prohibited path traversal characters.`,
      };
    }

    if (fileName.length > 255) {
      return {
        valid: false,
        error: `File "${fileName}" exceeds maximum filename length of 255 characters.`,
      };
    }

    // Double extension check (e.g. payload.php.png)
    if (SUSPICIOUS_DOUBLE_EXT_PATTERN.test(fileName)) {
      return {
        valid: false,
        error: `File "${fileName}" contains an unauthorized double extension.`,
      };
    }

    // Dangerous extension check
    if (DANGEROUS_EXTENSIONS_PATTERN.test(fileName)) {
      return {
        valid: false,
        error: `File "${fileName}" has a restricted or potentially dangerous extension.`,
      };
    }

    // Whitelist extension check
    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex === -1) {
      return {
        valid: false,
        error: `File "${fileName}" does not have a file extension. Only ${allowedExts.map((e) => e.toUpperCase()).join(", ")} files are permitted.`,
      };
    }

    const extension = fileName.substring(dotIndex + 1).toLowerCase();
    if (!allowedExts.includes(extension)) {
      return {
        valid: false,
        error: `File "${fileName}" has an invalid format (.${extension}). Only ${allowedExts.map((e) => e.toUpperCase()).join(", ")} files are allowed.`,
      };
    }
  }

  // 2. File Size Checks
  if (typeof file.size === "number") {
    if (file.size < minSize) {
      return {
        valid: false,
        error: `File "${fileName || "Selected file"}" is empty (0 bytes). Please select a valid document.`,
      };
    }

    if (file.size > maxSize) {
      const maxMb = Math.round(maxSize / (1024 * 1024));
      return {
        valid: false,
        error: `File "${fileName || "Selected file"}" exceeds the ${maxMb}MB size limit (${(file.size / (1024 * 1024)).toFixed(1)}MB).`,
      };
    }
  }

  // 3. MIME Type Whitelist Check (if reported by browser)
  if (file.type) {
    const fileMime = file.type.toLowerCase().trim();
    // Only check if mime type is provided (some OS file dialogs return empty type)
    if (fileMime && !allowedMimes.includes(fileMime)) {
      return {
        valid: false,
        error: `File "${fileName || "Selected file"}" has an unsupported MIME type (${fileMime}). Allowed types: PDF, JPG, PNG.`,
      };
    }
  }

  return { valid: true };
};

/**
 * Validates a list of files against individual limits and cumulative size limit.
 * @param {File[]} incomingFiles - Newly selected files
 * @param {File[]} existingStagedFiles - Files already staged
 * @param {Object} options - Custom validation options
 * @returns {{ validFiles: File[], errors: string[] }}
 */
export const validateDocumentBatch = (
  incomingFiles = [],
  existingStagedFiles = [],
  options = {}
) => {
  const maxTotalSize = options.maxTotalSize || MAX_TOTAL_UPLOAD_SIZE;
  const maxFileCount = options.maxFileCount || 20;

  const validFiles = [];
  const errors = [];

  let currentTotalSize = existingStagedFiles.reduce(
    (sum, f) => sum + (f.size || 0),
    0
  );

  if (existingStagedFiles.length + incomingFiles.length > maxFileCount) {
    errors.push(`Cannot upload more than ${maxFileCount} documents at once.`);
    return { validFiles, errors };
  }

  // Track filenames to prevent staging duplicate files
  const existingKeys = new Set(
    existingStagedFiles.map((f) => `${f.name}_${f.size}`)
  );

  for (const file of incomingFiles) {
    const key = `${file.name}_${file.size}`;
    if (existingKeys.has(key)) {
      errors.push(`File "${file.name}" is already staged for upload.`);
      continue;
    }

    const result = validateDocumentFile(file, options);
    if (!result.valid) {
      errors.push(result.error);
      continue;
    }

    if (currentTotalSize + (file.size || 0) > maxTotalSize) {
      const maxMb = Math.round(maxTotalSize / (1024 * 1024));
      errors.push(
        `Adding "${file.name}" would exceed the total batch upload limit of ${maxMb}MB.`
      );
      continue;
    }

    currentTotalSize += file.size || 0;
    existingKeys.add(key);
    validFiles.push(file);
  }

  return { validFiles, errors };
};
